import { useCart } from '../../context/CartContext';
import { deliveryAddressText } from '../../lib/deliveryAddressText';
import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, Search, User, MapPin, X } from "lucide-react";

import { Button } from "../ui/button";
import { get } from "../../lib/api";
import { customerHomePath } from "../../lib/customerHome";
const logo = "/og-image.png";
const CustomerLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isVendorStore = /^\/vendor\/[^/]+$/.test(location.pathname);
  const isDeliveryArea = /^\/food-delivery\/[^/]+(?:\/[^/]+)?\/?$/.test(location.pathname) || (/^\/[-a-z0-9]+\/?$/.test(location.pathname) && !['/food-delivery', '/auth', '/cart', '/checkout', '/profile', '/orders', '/legals', '/vendor-setup', '/account-verification'].includes(location.pathname.replace(/\/$/, ''))) || /^\/[-a-z0-9]+\/category\//.test(location.pathname);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const query = new URLSearchParams(location.search).get("q") || "";
    setSearchQuery(query);
  }, [location.search]);

  const handleSearch = (event) => {
    event.preventDefault();
    const query = searchQuery.trim();
    const searchPath = isVendorStore ? location.pathname : customerHomePath;
    const params = new URLSearchParams(isVendorStore ? location.search : "");
    if (query) params.set("q", query);
    else params.delete("q");
    navigate({ pathname: searchPath, search: params.toString() });
  };

  const clearSearch = () => {
    setSearchQuery('');
    const params = new URLSearchParams(location.search);
    if (!params.has('q')) return;
    params.delete('q');
    navigate({ pathname: location.pathname, search: params.toString(), hash: location.hash },
      { replace: true, state: location.state });
  };

  // --------------------------------------------------
  // Authentication state
  // --------------------------------------------------
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // --------------------------------------------------
  // Delivery location
  // --------------------------------------------------
  const [deliveryLocation, setDeliveryLocation] = useState("Not set");

  // --------------------------------------------------
  // Cart count
  // Replace this with your actual cart state/API later.
  // --------------------------------------------------
  const { items: savedCart } = useCart();
  const cartCount = savedCart.reduce((sum, item) => sum + item.quantity, 0);

  // --------------------------------------------------
  // Fetch authenticated customer information
  // --------------------------------------------------
  useEffect(() => {
    let isMounted = true;

    const fetchCustomer = async () => {
      const token = localStorage.getItem("token");

      // ----------------------------------------------
      // No token = guest user
      // ----------------------------------------------
      if (!token) {
        if (isMounted) {
          setIsAuthenticated(false);
          setDeliveryLocation("Not set");
          setAuthChecked(true);
        }

        return;
      }

      try {
        const response = await get("/customers/me");

        console.log("Fetch customer response:", response);

        // ----------------------------------------------
        // Invalid response / authentication failure
        // ----------------------------------------------
        if (!response?.success || !response?.customer) {
          console.warn("Customer authentication failed:", response?.message);

          localStorage.removeItem("token");

          if (isMounted) {
            setIsAuthenticated(false);
            setDeliveryLocation("Not set");
            setAuthChecked(true);
          }

          return;
        }

        const customer = response.customer;

        if (isMounted) {
          setIsAuthenticated(true);
        }

        // ----------------------------------------------
        // Get addresses
        // ----------------------------------------------
        const addresses = Array.isArray(customer?.addresses)
          ? customer.addresses
          : [];

        // ----------------------------------------------
        // Find default address
        // ----------------------------------------------
        const defaultLocation = addresses.find(
          (address) => address?.isDefault === true
        );

        if (!defaultLocation) {
          if (isMounted) {
            setDeliveryLocation("Not set");
          }

          return;
        }

        // ----------------------------------------------
        // Build readable location
        // ----------------------------------------------
        const locationParts = [
          defaultLocation?.sitio,
          defaultLocation?.barangay,
          defaultLocation?.municipality,
        ].filter(
          (value) =>
            value !== undefined && value !== null && String(value).trim() !== ""
        );

        if (isMounted) {
          setDeliveryLocation(
            deliveryAddressText(defaultLocation) || (locationParts.length > 0 ? locationParts.join(", ") : "Not set")
          );
        }
      } catch (error) {
        console.error("Error fetching customer information:", error);

        // ----------------------------------------------
        // If the API request failed, don't assume that
        // the user is authenticated.
        // ----------------------------------------------
        if (isMounted) {
          setIsAuthenticated(false);
          setDeliveryLocation("Not set");
        }

        /*
         * We only remove the token when the API indicates
         * that authentication has failed.
         *
         * Depending on how your `get()` helper handles
         * 401 errors, you may want to remove the token
         * here as well.
         */
      } finally {
        if (isMounted) {
          setAuthChecked(true);
        }
      }
    };

    fetchCustomer();

    return () => {
      isMounted = false;
    };
  }, []);

  // --------------------------------------------------
  // Render
  // --------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* ==================================================
          HEADER
          ================================================== */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-4">
          {/* ------------------------------------------------
              Logo
              ------------------------------------------------ */}
            <Link to={customerHomePath} className="flex flex-shrink-0 items-center">
              <span className="hidden text-2xl text-primary md:block">
                Big<b>Circle</b>
              </span>
              <img
                src={logo}
                alt="BigCircle"
                className="block h-14 w-auto object-contain md:hidden"
              />
            </Link>

          {/* ------------------------------------------------
              Delivery Location

              Only display this when authenticated.
              Guests will not see a fake delivery location.
              ------------------------------------------------ */}
          {isAuthenticated && (
            <Link
              to="/profile"
              className="
                hidden md:flex
                items-center gap-2
                text-sm text-gray-600
                bg-gray-100
                px-3 py-1.5
                rounded-full
                cursor-pointer
                hover:bg-gray-200
                transition
                min-w-0
              "
              title="Manage delivery address"
            >
              <MapPin className="w-4 h-4 text-primary flex-shrink-0" />

              <span className="truncate max-w-[250px]">
                Deliver to: {deliveryLocation}
              </span>
            </Link>
          )}

          {/* ------------------------------------------------
              Search
              ------------------------------------------------ */}
          <form
            className="flex-grow max-w-xl relative"
            role="search"
            onSubmit={handleSearch}
          >
            <button
              type="submit"
              aria-label={isVendorStore ? "Search this store's products" : "Search restaurants and products"}
              className="
                absolute
                left-3
                top-1/2
                -translate-y-1/2
                text-gray-400
                hover:text-primary
              "
            >
              <Search className="w-4 h-4" />
            </button>

            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search products, category etc..."
              aria-label={isVendorStore ? "Search this store's products and categories" : "Search products and categories"}
              className="
                w-full
                bg-gray-100
                border-none
                rounded-md
                py-2
                pl-10
                pr-10
                pr-4
                focus:ring-2
                focus:ring-primary/20
                text-sm
                outline-none
              "
            />
            {(searchQuery || new URLSearchParams(location.search).has('q')) && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </form>

          {/* ------------------------------------------------
              Actions
              ------------------------------------------------ */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* ==================================================
                CART
                ================================================== */}
            <Link to="/cart">
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                title="Shopping cart"
              >
                <ShoppingCart className="w-5 h-5" />

                {cartCount > 0 && (
                  <span
                    className="
                      absolute
                      -top-1
                      -right-1
                      bg-primary
                      text-white
                      text-[10px]
                      w-4
                      h-4
                      rounded-full
                      flex
                      items-center
                      justify-center
                    "
                  >
                    {cartCount > 99 ? "99+" : cartCount}
                  </span>
                )}
              </Button>
            </Link>

            {/* ==================================================
                AUTHENTICATION ACTIONS
                ================================================== */}

            {authChecked && isAuthenticated ? (
              <>
                {/* ----------------------------------------------
                    Profile
                    ---------------------------------------------- */}
                <Link to="/profile">
                  <Button variant="ghost" size="icon" title="Profile">
                    <User className="w-5 h-5" />
                  </Button>
                </Link>
              </>
            ) : (
              /* ----------------------------------------------
                 Guest / Sign In
                 ---------------------------------------------- */
              authChecked && (
                <Link to="/auth" state={{ from: location.pathname + location.search + location.hash, returnState: location.state }}>
                  <Button
                    variant="outline"
                    className="
                      flex
                      items-center
                      gap-2
                      px-3
                    "
                  >
                    <User className="w-4 h-4" />

                    <span className="hidden sm:inline">Sign In</span>
                  </Button>
                </Link>
              )
            )}
          </div>
        </div>
      </header>

      {/* ==================================================
          MAIN CONTENT
          ================================================== */}
      <main className={location.pathname.replace(/\/$/, '') === '/food-delivery' ? 'flex-grow container mx-auto px-0 pt-0 pb-3 md:px-4' : isDeliveryArea ? 'flex-grow container mx-auto px-0 pt-0 pb-6 md:px-4 md:py-6' : 'flex-grow container mx-auto px-4 py-6'}>
        <Outlet />
      </main>

      {/* ==================================================
          FOOTER
          ================================================== */}
      <footer className="bg-white border-t py-8">
        <div className="container mx-auto px-4 text-center text-gray-500 text-sm">
          <p>© 2026 BigCircle. All rights reserved.</p>
          <a
            href="https://www.facebook.com/profile.php?id=61593698674382"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block text-primary hover:underline focus-visible:underline"
          >
            Follow BigCircle on facebook
          </a>
        </div>
      </footer>
    </div>
  );
};

export default CustomerLayout;
